import { useEffect, useState } from "react"
import { useQuery } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import { TAdminActions } from "@fuse/sharedComponents/buttons/BottomAction"
import { useAlert } from "@fuse/sharedComponents/globalAlert"
import { useImageModal } from "@fuse/sharedComponents/globalImageModal"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import useShare from "@fuse/sharedComponents/globalShare/useShare"
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  ActionItem,
  AllowedPathFromEventPage,
  EButtonType,
  EIcons,
  ETankStackKeys,
  EVENT_CLASS,
  EVENT_STATUS,
  EventDocData,
  EventFunctionMap,
  EventStatusType,
  navigation,
  Question,
  TDisplayFormat,
  TWebUser,
} from "@letshang/constants"
import { reload, signOut } from "firebase/auth"
import { find, get, includes, isEmpty, map, size } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { defaultUser, screenState, userState } from "@fuse/recoil/signup"
import { userStateForAnalytics } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"
import { auth } from "@firebaseConfig/clientApp"

import {
  claimTicketForEvent,
  fetchGuestListForEvent,
  getAcceptedGuestAvatarsApi,
  getEventDetails,
} from "@api/firebaseWrappedApis"
import { getUserInfo } from "@api/firebaseWrappedApis"
import {
  useJoinEvent,
  useJoinWaitlistForEvent,
  useLeaveEvent,
  useLeaveNonTicketedEvent,
} from "@api/services/events/useEventsApi"

import { DeepLinkPath, generateDeepLink } from "@fuse/utils/DeviceDection"
import {
  DownloadLinkMap,
  isDev,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

import { IAvatarData } from "@fuse/pages/eventPage/components/AvatarGroup"
import useFetchEventDetails from "@fuse/pages/eventPage/useFetchEventDetails"
import { useBuyTicketsSession } from "@fuse/pages/eventSignUp/BuyTickets/buyTicketsSessionManager"

import PleadingGIF from "@fuse/assets/gif/gif-pleading.gif"
import upsideDownFace from "@fuse/assets/gif/gif-upside-down-face.gif"

const leaveEventsOptions = [
  EventStatusType.UNDER_REVIEW,
  EventStatusType.ACCEPTED,
  EventStatusType.ATTENDING,
  EventStatusType.OPENED_EXTERNAL_LINK,
  EventStatusType.NOT_ATTENDING_EXTERNAL_EVENT,
]

export enum WhichBtnState {
  Invited = "invited",
  Accepted = "accepted",
  Initiator = "initiator",
  NotInvited = "not_invited",
  Left = "left",
  UnderReview = "under_review",
  OpenedExternalLink = "opened_external_link",
  NotAttendingExternalEvent = "not_attending_external_event",
}

export enum EActionTitle {
  Share = "Share",
  Open = "Open",
  NotAttending = "Not Attending",
  GetTickets = "Get tickets",
  MyTicket = "My ticket",
  Join = "Join",
  AddPlusOnes = "Add +1s",
  EditPlusOnes = "Edit +1s",
  WaitForApproval = "⏳ Waiting for approval",
  Declined = "Declined",
  RefundRequested = "Refund Requested",
  Refunded = "Refunded",
  Cancel = "Cancel Event",
}

type EventInfoStateMapType = {
  [key in EventStatusType]?: TAdminActions[]
}

export const useEvents = (
  eventId: string,
  uid?: string,
  invitedId?: string,
) => {
  const navigate = useNavigate()
  const [, setStep] = useRecoilState(screenState)
  const [user, setUser] = useRecoilState(userState)
  const userStateForAnalyticsValue = useRecoilValue(userStateForAnalytics)

  const location = useLocation()
  const device = useDeviceDetection()
  const downloadLink = DownloadLinkMap[device]

  const shareContent = useShare()
  const { showAlert } = useAlert()
  const { showImageAlert, hideImageAlert } = useImageModal()
  const { state, refresh } = useFetchEventDetails(eventId)
  const [isEmailInboxVerification, setIsEmailInboxVerification] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpenGuestList, setIsOpenGuestList] = useState(false)
  const [isVerificationPopup, setIsVerificationPopup] = useState(false)

  const userEmail = get(user, "email", "")
  const needSignUp = !user.isLoggedIn
  // const isUserEmailExist = !isEmpty(userEmail);
  const [isUserEmailExist, setIsUserEmailExist] = useState(true)
  const [refundAlertVisible, setRefundAlertVisible] = useState(false)

  const [isNonTicketedReasonPopupOpen, setIsNonTicketedReasonPopupOpen] =
    useState<boolean>(false)
  const [isCancelEventModalOpen, setIsCancelEventModalOpen] =
    useState<boolean>(false)
  const { clearAllBuyTicketsSession } = useBuyTicketsSession()

  // Use consolidated email verification
  const { verifyEmail, startPolling } = useEmailVerification()

  // const [, , removeTicketsFormData] = useSessionStorage(
  //   EBuyTicketSessionKeys.TICKETS_FORM_DATA,
  //   [],
  // )
  // const [, , removeEventsTickets] = useSessionStorage(
  //   EBuyTicketSessionKeys.EVENTS_TICKETS,
  //   [],
  // )
  // const [, , removeGroupTicketsInfoState] = useSessionStorage(
  //   EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE,
  //   [],
  // )

  const handleOpenNonTicketedReasonPopup = () => {
    setIsNonTicketedReasonPopupOpen(true)
  }

  const handleCloseNonTicketedReasonPopup = () => {
    setIsNonTicketedReasonPopupOpen(false)
  }

  const handleOpenCancelModal = () => {
    setIsCancelEventModalOpen(true)
  }

  const handleCloseCancelModal = () => {
    setIsCancelEventModalOpen(false)
  }

  const handleEventCancelled = () => {
    // Refresh event data or redirect after cancellation
    refresh()
    handleCloseCancelModal()
  }

  const {
    mutate,
    data: leaveEventData,
    error,
    isLoading: leaveEventLoading,
  } = useLeaveEvent()

  const eventDetail = state.eventDetails
  const showSoldOut = eventDetail?.eventDocData?.showSoldOut
  const howManyPeople = get(eventDetail, "eventDocData.howManyPeople", 0) as any
  const numberAttending = get(eventDetail, "eventDocData.numberAttending", 0)

  const noOfRemainingSpots = howManyPeople - numberAttending

  const { mutate: mutateJoinEvent, isLoading: joinEventLoading } = useJoinEvent(
    invitedId,
    eventDetail?.eventDocData?.externalEventUrl,
  )

  const baseQuestionnaires: Question[] = get(
    eventDetail,
    "eventDocData.questionnaires",
    [],
  )

  const {
    mutate: mutateLeaveNonTicketedEvent,
    isLoading: leaveNonTicketedEventLoading,
  } = useLeaveNonTicketedEvent()

  const fromScreen = get(location, "state.from", null)
  const isFromCheckOut = fromScreen === "StripeCheckout"
  const isFromCreateEventSuccess = fromScreen === ROUTES.EVENT_CREATE_SUCCESS

  const {
    fastSignUpStage,
    setFastSignUpStage,
    setShowFastSignUpPopUp,
    eventParams,
    showFastSignUpPopUp,
  } = useSignUpModalState()

  // Fetch Events
  useEffect(() => {
    // removeTicketsFormData()
    // removeEventsTickets()
    // removeGroupTicketsInfoState()
    localStorage.setItem("hasVisitedEventsPage", eventId)
    const isInSignupFlow = window.location.pathname.includes("/signup")
    const isInSignupModal = showFastSignUpPopUp || fastSignUpStage !== null

    if (isInSignupFlow || isInSignupModal) {
      return
    }

    clearAllBuyTicketsSession()

    // refresh()
  }, [eventId])

  const {
    data: acceptedGuestAvatars = [],
    isLoading: acceptedGuestAvatarsLoading,
  } = useQuery(
    [ETankStackKeys.EVENT_ACCEPTED_GUEST_AVATARS, eventId],
    async () => {
      try {
        const res = await getAcceptedGuestAvatarsApi({ eventId })
        return res
      } catch (error) {
        console.error("Failed to fetch invited list data:", error)
      }
    },
    {
      onSettled: () => {},
      refetchOnWindowFocus: false,
    },
  )

  const {
    data: guestList = {},
    refetch: refetchGuestList,
    isLoading: isGuestListLoading,
  } = useQuery(
    [ETankStackKeys.EVENT_MEMBER_LIST, eventId],
    async () => {
      try {
        const res = (await fetchGuestListForEvent({ eventId })) as any
        return res.data || []
      } catch (error) {
        console.error("Failed to fetch invited list data:", error)
      }
    },
    {
      onSettled: () => {},
      refetchOnWindowFocus: false,
    },
  )

  useEffect(() => {
    const user = auth.currentUser

    if (user) {
      const isUserEmailExist = !isEmpty(userEmail) && user.emailVerified

      setIsUserEmailExist(isUserEmailExist)
      user
        .reload()
        .then(() => {
          if (isEmailInboxVerification && user.emailVerified) {
            setIsVerificationPopup(false)
          }
        })
        .catch((error) => {
          console.error("Error reloading user: ", error)
        })
    }
  }, [auth, auth.currentUser])

  const loggedInUserId = get(user, "uid", "")
  const eventDocData: EventDocData | undefined = get(
    eventDetail,
    "eventDocData",
  )
  const isAdmin = eventDocData?.initiatorUid === loggedInUserId

  const plusOnes = get(eventDocData, "addPlusOnes", {}) as {
    isAllowed: boolean
    allowedNumber: number[]
  }

  const eventStatus = get(
    eventDetail,
    "eventDocData.eventStatus",
    EVENT_STATUS.DRAFT,
  ) as EVENT_STATUS
  const isEventLive = eventStatus === EVENT_STATUS.OPEN
  const isDraftEvent = eventStatus === EVENT_STATUS.DRAFT
  const externalEventUrl = eventDocData?.externalEventUrl

  const eventClass = get(
    eventDocData,
    "eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  const myJoinStatus = get(eventDetail, "myJoinStatus", WhichBtnState.Invited)
  const myBoughtTickets = get(eventDetail, "myTickets", [])
  const myTicket = find(myBoughtTickets, function (ticket) {
    return get(ticket, "attendee", "") === loggedInUserId
  })
  const numberOfExtraJoinee = get(eventDetail, "numberOfExtraJoinee", 0)

  const [plusOnePopupState, setPlusOnePopupState] = useState<{
    isOpen: boolean
    isLoading: boolean
    showAlert: boolean
    plusOneCount: number
    isEditMode: boolean
  }>({
    isOpen: false,
    isLoading: false,
    showAlert: false,
    plusOneCount: numberOfExtraJoinee,
    isEditMode: numberOfExtraJoinee > 0,
  })

  useEffect(() => {
    setPlusOnePopupState({
      isOpen: false,
      isLoading: false,
      showAlert: false,
      plusOneCount: numberOfExtraJoinee,
      isEditMode: numberOfExtraJoinee > 0,
    })
  }, [numberOfExtraJoinee])

  const myTicketId = get(myTicket, "ticketId", "")
  const myTicketStatus: EventStatusType = isAdmin
    ? ((isEventLive
        ? EVENT_STATUS.OPEN
        : EventStatusType.INITIATOR) as EventStatusType)
    : get(myTicket, "status", EventStatusType.INVITED)

  const isDeclined = myJoinStatus === EventStatusType.DECLINED

  const isRefunded = myTicketStatus === EventStatusType.REFUNDED
  const isAccepted =
    myTicketStatus === EventStatusType.ATTENDING ||
    myTicketStatus === EventStatusType.ACCEPTED

  const refundData = get(eventDetail, "refundData", {})
  const refundReferenceNumber = get(
    refundData,
    "refundDetails.card.reference",
    "",
  )
  const isRefundInProgress = myTicketStatus === EventStatusType.REFUND_REQUESTED
  const isUnderReview = myTicketStatus === EventStatusType.UNDER_REVIEW

  const questionnaires: Question[] = get(
    eventDetail,
    "eventDocData.questionnaires",
    [],
  )
  const isQuestionnairesNeeded = size(questionnaires) > 0

  useEffect(() => {
    if (!isEmpty(eventDocData) && !isAdmin)
      logAnalyticEvent(ELogAnalyticEvent.EVENT_DETAILS_WEB, {
        uid: loggedInUserId,
        page_name: "Event details",
        event_id: eventId,
        event_name: get(eventDocData, "eventName", ""),
        event_type: isTicketedEvent ? "Ticketed" : "non-ticketed",
      })
  }, [userStateForAnalyticsValue, eventDocData])

  useEffect(() => {
    if (refundReferenceNumber) {
      setRefundAlertVisible(true)
      // showImageAlert({
      //   image: moneyWithWings,
      //   description:
      //     "Your refund is on its way and should arrive in 5-10 business days!\n\nIf you have any questions or issues, here's your reference ID:\n\n pi_1GR2ma2eZvKYlo2CE5xQkL  􀉁\n\nWe hope to see you at another event soon!",
      //   okButtonTitle: "Download the app",
      //   okButtonPress: () => onOpenDownloadLink(downloadLink),
      // });
    }
  }, [refundReferenceNumber])

  useEffect(() => {
    if (fastSignUpStage === "done") {
      if (showSoldOut) {
        console.log("showSoldOut", showSoldOut)
        const actionToExecute = myEventActions.find(
          (action) => action.isExecutableAfterFastSignup,
        )
        console.log("actionToExecute==>", JSON.stringify(actionToExecute))
        if ("onPress" in actionToExecute) {
          actionToExecute.onPress()
        }
      } else {
        if (!isTicketedEvent) {
          logAnalyticEvent(ELogAnalyticEvent.QUICK_SIGN_UP_WEB, {
            uid: loggedInUserId,
            page_name: !isFromCreateEventSuccess
              ? "Event details - quick sign up"
              : "Create event - quick sign up",
            event_id: eventId,
            event_name: get(eventDocData, "eventName", ""),
            event_type: "non-ticketed",
          })
          !isFromCreateEventSuccess && onEventJoin(eventParams)
        } else {
          logAnalyticEvent(ELogAnalyticEvent.QUICK_SIGN_UP_WEB, {
            uid: loggedInUserId,
            page_name: "Choose Ticket - quick sign up",
            event_id: eventId,
            event_name: get(eventDocData, "eventName", ""),
            event_type: "Ticketed",
          })
        }
      }
      refresh()
      setFastSignUpStage(null)
    }
  }, [fastSignUpStage])

  const forceReload = () => {
    window.location.reload()
  }

  const clearState = () => {
    navigate(".", { replace: true, state: null }) // Clear the state
  }

  useEffect(() => {
    if (isFromCheckOut) {
      setTimeout(() => {
        // forceReload()
        clearState()
      }, 1000)
    }
  }, [isFromCheckOut])

  // useEffect(() => {
  //   if (isUnderReview && isFromCheckOut) {
  //     showImageAlert({
  //       image: TicketConfirmIllustrator,
  //       title: "Ticket confirmed",
  //       description:
  //         "Now we need to wait for the host to approve you. Once you're approved you'll have access to your ticket.",
  //       okButtonTitle: "Got it",
  //       okButtonPress: () => {
  //         console.log("OK button pressed");
  //       },
  //     });
  //   }
  // }, [isFromCheckOut, isUnderReview]);

  const hasNotJoined =
    !isAdmin &&
    [
      EventStatusType.INVITED,
      EventStatusType.DECLINED,
      EventStatusType.LEFT,
      EventStatusType.UNDER_REVIEW,
      EventStatusType.NOT_INVITED,
      EventStatusType.NOT_ATTENDING_EXTERNAL_EVENT,
      EventStatusType.QUEUED,
    ].includes(myJoinStatus as EventStatusType)

  const onShareEvent = () => {
    generateDeepLink({
      id: eventId,
      path: DeepLinkPath.Events,
    }).then((results: { shortLink: string }) => {
      shareContent({
        text: "Share this event",
        url: `https://${results?.shortLink}`,
        title: "Let's Hang!",
        type: DeepLinkPath.Events,
      })
    })
  }

  const handleShare = () => {
    if (isDraftEvent && !isAdmin) {
      showImageAlert({
        imageText: "⚠️",
        description: isAdmin
          ? "You're about to share an unpublished draft. Keep in mind people won't be able to join until you publish your event."
          : "You're about to share an unpublished draft. Keep in mind people won't be able to join until host publish event.",
        okButtonTitle: "Got it!",
        okButtonPress: () => {
          hideImageAlert(), onShareEvent()
        },
      })
    } else {
      onShareEvent()
    }
  }

  const WaitingForApprovalActions = {
    title: EActionTitle.WaitForApproval,
    onPress: () => {},
    disabled: false,
    buttonVariant: EButtonType.outline,
  }

  const approvedForEventActions = [
    {
      title: EActionTitle.MyTicket,
      disabled: false,
      iconName: EIcons.ticketOutlineIcon,
      onPress: () => {
        let path = ROUTES.TICKETS.replace(":eventId", eventId)
        if (!eventDetail?.isGroupBuyer) {
          path += `/${ROUTES.MY_TICKET_ID}`
          path = path.replace(":attendeeId", myTicket?.attendee)
        }
        navigate(path, {
          state: {
            data: eventDetail,
          },
        })
      },
    },
    // {
    //   title: "Share event",
    //   buttonType: "EButtonType.outline",
    //   iconName: EIcons.shareEventIcon,
    //   buttonColor: "transparent",
    //   labelClassName: "text-primary",
    //   onPress: () => handleShare(),
    // },
  ]

  const onLeaveEventAction = () => {
    const payload = {
      eventId: eventId,
    }
    mutate(payload, {
      onSuccess: (data) => {
        const refundRequired = get(data, "data.refundRequired", false)
        refresh()
        if (refundRequired) {
          showImageAlert({
            image: upsideDownFace,
            description:
              "We've issued your ticket refund. It usually processes in around 5-10 business days.\n\nThank you for your understanding!",
            okButtonTitle: "OK",
            okButtonPress: () => {
              // Dialog will close automatically
            },
          })
        }
      },
      onError: (err: any) => {
        showErrorToast(`Error: ${err.message}`)
      },
    })
  }

  const leaveEventDescriptions = !isTicketedEvent
    ? "Are you sure you want to leave this event? Keep in mind, once you leave, you won't be able to rejoin."
    : isAccepted
      ? "Are you sure you want to leave this event?\n\n Since you've already been approved,\n please note that <hl>refunds are not available</hl>"
      : "Are you sure you want to leave this event? Keep in mind, once you leave, you won't be able to rejoin.\n\nIf you cancel before your spot is confirmed, we'll process your refund."

  const onLeaveEvent = () => {
    showImageAlert({
      image: PleadingGIF,
      description: leaveEventDescriptions,
      okButtonTitle: "Cancel",
      okButtonPress: () => {
        hideImageAlert()
        console.log("OK button pressed")
      },
      cancelButtonTitle: "Leave event",
      cancelButtonPress: onLeaveEventAction,
    })
  }

  const onLeaveNonTicketedEvent = () => {
    showImageAlert({
      image: PleadingGIF,
      description: leaveEventDescriptions,
      okButtonTitle: "Cancel",
      okButtonPress: () => {
        hideImageAlert()
        console.log("OK button pressed")
      },
      cancelButtonTitle: "Leave event",
      cancelButtonPress: handleOpenNonTicketedReasonPopup,
    })
  }

  const onConfirmLeaveNonTicketedEvent = (leaveReason: string) => {
    const payload = {
      eventId: eventId,
      leaveReason,
    }
    mutateLeaveNonTicketedEvent(payload, {
      onSuccess: () => {
        handleCloseNonTicketedReasonPopup()
        refresh()
      },
      onError: (err: any) => {
        showErrorToast(`Error: ${err.message}`)
      },
    })
  }

  const handleSignupClick = () => {
    setUser((prevUser: TWebUser) => ({
      ...prevUser,
      redirectUrl: location.pathname,
      eventId: eventId,
      invitedId,
      isTicketedEvent,
    }))
    // navigate(ROUTES.SIGNUP);
    // setStep(1);
    setShowFastSignUpPopUp(true)
  }

  //dummy functions for dummy statuses
  const declinedForEventActions = [
    {
      title: EActionTitle.Declined,
      onPress: () => {},
      disabled: true,
    },
  ]

  const refundRequestedForEventActions = [
    {
      title: EActionTitle.RefundRequested,
      onPress: () => {},
      disabled: true,
    },
  ]

  const refundedForEventActions = [
    {
      title: EActionTitle.Refunded,
      onPress: () => {},
      disabled: true,
    },
  ]

  const onEventJoin = (eventParams = {}) => {
    setIsOpenGuestList(false)

    // Check if event is in draft mode
    if (eventDetail?.eventDocData?.eventStatus === EVENT_STATUS.DRAFT) {
      showInfoToast("This event is currently in draft mode")
      return
    }

    if (isTicketedEvent) {
      if (totalTicketQuantity - totalPurchasedTickets <= 0) {
        showInfoToast("No tickets left. Event is sold out!")
        return
      }
      navigate(ROUTES.CHOOSE_TICKET.replace(":eventId", eventId), {
        state: {
          invitedId,
        },
      })
    } else if (needSignUp) {
      showImageAlert({
        imageText: "ℹ️",
        title: "Contact information",
        description:
          "By signing up for this event, your <hl>name</hl> and <hl>phone number</hl> will automatically be shared with the event host for coordination purposes. \n\nThis helps the host keep you informed and updated about any event details or changes.",
        okButtonTitle: "Got it",
        okButtonPress: () => {
          hideImageAlert(), handleSignupClick()
        },
        cancelButtonTitle: "Cancel joining",
        cancelButtonPress: () => {
          console.log("cancel button pressed")
        },
        cancelButtonClassname:
          "!bg-transparent !border-transparent sm:!bg-white/10 sm:!border-white/20",
        cancelButtonTextClassname: "!text-white/35 sm:!text-white",
      })
    } else if (isQuestionnairesNeeded) {
      const navToUpdated =
        eventDetail?.eventDocData?.questionnaireDisplayFormat ===
        TDisplayFormat.SINGLE_PAGE
      const navigationRoute = navToUpdated
        ? ROUTES.FILL_QUESTIONNAIRE_UPDATED
        : ROUTES.FILL_QUESTIONNAIRE
      navigate(navigationRoute.replace(":eventId", eventId), {
        state: {
          invitedId,
          attendeeQuestioners: baseQuestionnaires,
        },
      })
    } else {
      goToJoin(eventParams)
    }
  }

  const goToJoin = async (eventParams = {}) => {
    if (joinEventLoading) return

    // Check if user is host or event is closed
    const isHost =
      myJoinStatus === EventStatusType.INITIATOR ||
      eventDocData?.initiatorUid === loggedInUserId
    if (eventDocData?.eventStatus === EVENT_STATUS.CLOSE) {
      showInfoToast(
        "This event is closed and no longer accepting new participants",
      )
      return
    }

    if (isHost) {
      showInfoToast("You are the host of the event")
      return
    }
    if (myJoinStatus === EventStatusType.ACCEPTED) {
      showInfoToast("You have already joined the event")
      return
    }
    if (myJoinStatus === EventStatusType.UNDER_REVIEW) {
      showInfoToast("Your request to join is already under review")
      return
    }
    if (myJoinStatus === EventStatusType.REFUNDED) {
      showInfoToast("Your money is refunded for this event")
      return
    }
    if (myJoinStatus === EventStatusType.REFUND_REQUESTED) {
      showInfoToast(
        "You have already placed a request for a refund for this event",
      )
      return
    }

    if (noOfRemainingSpots <= 0) {
      showInfoToast("Event is sold out!")
      return
    }

    const payload = {
      eventId: eventId,
      numberOfExtraJoinee: (eventParams as any)?.plusOneCount,
    }
    await mutateJoinEvent(payload, {
      onSuccess: (data) => {
        const isSuccess = get(data, "success", false)
        if (isSuccess) {
          const message = get(data, "message", "Something went wrong!")
          refresh()

          // Update plugin join status immediately
          if ((window as any).updatePluginJoinStatus) {
            ;(window as any).updatePluginJoinStatus(true)
          }

          showSuccessToast(
            message ?? `You successfully joined "${eventDocData?.eventName}"`,
          )
        } else {
          const message = get(data, "message", "Something went wrong!")
          showErrorToast(`Error: ${message}`)
        }
      },
      onError: (err: any) => {
        showErrorToast(`Error: ${err.message}`)
      },
    })
  }

  const totalTicketQuantity = get(eventDetail, "totalTicketQuantity", 0)
  const totalPurchasedTickets = get(eventDetail, "totalPurchasedTickets", 0)

  const iconName = externalEventUrl
    ? EIcons.joinExternalEventIcon
    : EIcons.joinShareForwardIcon

  // here
  const BuyEventActions = [
    {
      title: EActionTitle.GetTickets,
      iconName: EIcons.joinShareForwardIcon,
      onPress: () => {
        if (totalTicketQuantity - totalPurchasedTickets <= 0) {
          showInfoToast("No tickets left. Event is sold out!")
          return
        }
        if (!eventDetail?.eventDocData?.showSoldOut && hasNotJoined) {
          navigate(ROUTES.CHOOSE_TICKET.replace(":eventId", eventId), {
            state: { invitedId },
          })
        }
      },
    },
  ]

  // here
  const JoinEventAction = {
    title: EActionTitle.Join,
    onPress: () => onEventJoin(),
    iconName: iconName,
    isLoading: joinEventLoading,
  }
  const {
    mutateAsync: joinWaitlistFn,
    data: joinWaitlistData,
    isLoading: joinWaitlistFnLoading,
  } = useJoinWaitlistForEvent({
    eventId,
    uid: user?.uid,
  })

  useEffect(() => {
    if (
      (joinWaitlistData &&
        (joinWaitlistData as { success: boolean })?.success === true) ||
      (joinWaitlistData as { success: boolean })?.success === false
    ) {
      refresh()
    }
  }, [joinWaitlistData])

  const ShareEventAction = { title: EActionTitle.Share, onPress: handleShare }
  const CancelEventAction = {
    title: EActionTitle.Cancel,
    onPress: handleOpenCancelModal,
    isDestructive: true,
  }
  const openExtarnalLinkFn = () => {
    window.open(externalEventUrl, "_blank", "noopener,noreferrer")
  }
  const OpenExternalLinkBtnProp = {
    title: EActionTitle.Open,
    onPress: openExtarnalLinkFn,
  }
  const ConfirmLeaveNonTicketedEvent = {
    title: EActionTitle.NotAttending,
    onPress: () => onConfirmLeaveNonTicketedEvent(""),
  }
  const PlusOneActions = {
    title:
      plusOnePopupState.plusOneCount > 0
        ? EActionTitle.EditPlusOnes
        : EActionTitle.AddPlusOnes,
    onPress: () => {
      const showAlert =
        numberOfExtraJoinee > 0 &&
        myJoinStatus === EventStatusType.ACCEPTED &&
        eventDetail?.eventDocData?.requiredApproval
      const isOpen = true

      setPlusOnePopupState({ ...plusOnePopupState, isOpen, showAlert })
    },
  }
  const EventInfoStateMap: EventInfoStateMapType = {
    [EventStatusType.LEFT]: [],
    [EventStatusType.NOT_ATTENDING_EXTERNAL_EVENT]: [],
    [EventStatusType.CO_HOST]: [],
    [EventStatusType.USER_DECLINED]: [],
    [EventStatusType.INVITED]: BuyEventActions,
    [EventStatusType.NOT_INVITED]: BuyEventActions,
    [EventStatusType.UNDER_REVIEW]: [WaitingForApprovalActions],
    [EventStatusType.ATTENDING]: approvedForEventActions,
    [EventStatusType.CHECKED_IN]: approvedForEventActions,
    [EventStatusType.ACCEPTED]: approvedForEventActions,
    [EventStatusType.OPENED_EXTERNAL_LINK]: approvedForEventActions,
    [EventStatusType.DECLINED]: declinedForEventActions,
    [EventStatusType.INITIATOR]: [ShareEventAction],
    [EventStatusType.REFUND_REQUESTED]: refundRequestedForEventActions,
    [EventStatusType.REFUNDED]: refundedForEventActions,
  }

  const NonTicketedEventInfoStateMap = {
    // Jason - Likely we need to add an option here for external events, but this code doesn't make sense to me so I'm not editing it
    [WhichBtnState.Invited]: [JoinEventAction],
    [WhichBtnState.NotInvited]: [JoinEventAction],
    [WhichBtnState.Left]: [JoinEventAction],
    [WhichBtnState.NotAttendingExternalEvent]: [JoinEventAction],

    [WhichBtnState.OpenedExternalLink]: [
      OpenExternalLinkBtnProp,
      ShareEventAction,
      ConfirmLeaveNonTicketedEvent,
    ],
    [WhichBtnState.Initiator]: [ShareEventAction],

    [WhichBtnState.Accepted]: plusOnes?.isAllowed
      ? [ShareEventAction, PlusOneActions]
      : [ShareEventAction],

    [WhichBtnState.UnderReview]: plusOnes?.isAllowed
      ? [WaitingForApprovalActions, PlusOneActions]
      : [WaitingForApprovalActions],
  }

  const getPropsNameToPass = (routeName: AllowedPathFromEventPage) => {
    let exactPath = routeName as string
    let propsToPass = {}
    switch (routeName) {
      case "CHOOSE_TICKET":
        exactPath = ROUTES.CHOOSE_TICKET.replace(":eventId", eventId)
        propsToPass = {
          state: { invitedId },
        }
        break

      case "FILL_QUESTIONNAIRE":
        exactPath = ROUTES.FILL_QUESTIONNAIRE.replace(":eventId", eventId)
        propsToPass = {
          state: {
            invitedId,
            attendeeQuestioners: baseQuestionnaires,
          },
        }
        break

      case "FILL_QUESTIONNAIRE_UPDATED":
        exactPath = ROUTES.FILL_QUESTIONNAIRE.replace(":eventId", eventId)
        propsToPass = {
          state: {
            invitedId,
            attendeeQuestioners: baseQuestionnaires,
          },
        }
        break

      case "MY_TICKET":
        exactPath = ROUTES.TICKETS.replace(":eventId", eventId)
        if (!eventDetail?.isGroupBuyer) {
          exactPath += `/${ROUTES.MY_TICKET_ID}`
          exactPath = exactPath.replace(":attendeeId", myTicket?.attendee)
        }
        break
      default:
        exactPath = ROUTES.LANDING
    }
    return { exactPath, propsToPass }
  }

  const constructOverridingFunction = (overrides: ActionItem[]) => {
    const actionArr = []
    overrides.forEach((i) => {
      const { onPressData, ...rest } = i
      switch (onPressData.type) {
        case EventFunctionMap.EXTERNAL_NAV:
          actionArr.push({
            ...rest,
            onPress: openExtarnalLinkFn,
          })
          break
        case EventFunctionMap.SHARE:
          actionArr.push({
            ...rest,
            onPress: handleShare,
          })
          break
        case EventFunctionMap.PLUS_ONES:
          actionArr.push(PlusOneActions)
          break
        case EventFunctionMap.LEAVE_EVENT:
          actionArr.push({
            ...rest,
            onPress: () => onConfirmLeaveNonTicketedEvent(""),
          })
          break
        case EventFunctionMap.NAVIGATE:
          const navData = onPressData as navigation
          const { exactPath, propsToPass } = getPropsNameToPass(navData.path)
          actionArr.push({
            ...rest,
            onPress: () => {
              navigate(exactPath, propsToPass)
            },
          })
          break
        case EventFunctionMap.JOIN_EVENT:
          actionArr.push({
            ...rest,
            onPress: onEventJoin,
          })
          break
        case EventFunctionMap.JOIN_WAITLIST:
          const action = needSignUp ? handleSignupClick : joinWaitlistFn
          actionArr.push({
            ...rest,
            onPress: action,
          })
          break

        case EventFunctionMap.EMPTY:
        default:
          actionArr.push({
            ...rest,
            onPress: () => {},
          })
      }
    })

    return actionArr
  }
  /**
   * example : we get the eventActionOverRide as [{
   * title:"bla bla", onPressActionType:"joinWishlist", disabled:false}]
   * */

  const eventActionOverride = eventDetail?.eventActionOverride || []
  const eventActionOverridenFunctionSet =
    constructOverridingFunction(eventActionOverride)

  const myEventActions = eventActionOverridenFunctionSet?.length
    ? eventActionOverridenFunctionSet
    : !isAdmin && isDraftEvent
      ? []
      : isTicketedEvent
        ? EventInfoStateMap[myJoinStatus as EventStatusType]
        : NonTicketedEventInfoStateMap[myJoinStatus as WhichBtnState]
  const canLeaveEvent =
    includes(leaveEventsOptions, myTicketStatus) ||
    includes(
      [WhichBtnState.Accepted, WhichBtnState.UnderReview],
      myJoinStatus as WhichBtnState,
    )

  const onVerifyEmail = async (email: string) => {
    checkVerificationState()

    const result = await verifyEmail(email, {
      onSuccess: () => {
        // Success callback for both dev and production flows
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, close UI
        setIsVerificationPopup(false)
        setIsEmailInboxVerification(false)
      },
      onRequireInboxCheck: () => {
        // Production mode: show inbox check UI and start polling
        setIsEmailInboxVerification(true)

        // Start polling with cleanup
        startPolling(() => {
          setIsUserEmailExist(true)
          setIsVerificationPopup(false)
          setIsEmailInboxVerification(false)
        })
      },
      onVerificationComplete: () => {
        // Handle completion callback if needed
      },
    })

    return result
  }

  // Function to check email verification status periodically
  const checkVerificationState = async () => {
    // Set an interval to reload the Firebase auth state every 2 seconds
    const intervalId = setInterval(async () => {
      // eslint-disable-next-line no-extra-semi
      ;(await auth.currentUser) && reload(auth.currentUser!)
      if (auth.currentUser?.emailVerified) {
        setIsUserEmailExist(true)
        setIsVerificationPopup(false)
        setIsEmailInboxVerification(false)
        clearInterval(intervalId) // Stop checking if email is verified
      }
    }, 2000)

    // Clear the interval after 30 seconds (to prevent memory leaks)
    setTimeout(() => {
      clearInterval(intervalId)
    }, 30000)
  }

  const mapAcceptedGuestAvatars = (
    acceptedGuestAvatars: string[],
  ): IAvatarData[] => {
    return map(acceptedGuestAvatars, (obj) => ({
      avatar: obj,
    }))
  }

  const validateAcceptedGuestAvatars = mapAcceptedGuestAvatars(
    acceptedGuestAvatars as string[],
  )

  const claimTicket = async () => {
    const response: any = await claimTicketForEvent({ eventId })
    if (response?.success && response?.data?.isClaimed) {
      let path = ROUTES.TICKETS.replace(":eventId", eventId)
      path += `/${ROUTES.MY_TICKET_ID}`
      path = path.replace(":attendeeId", response?.data.ticketId)
      navigate(path)
    } else {
      showErrorToast("Something went wrong.")
    }
  }

  const onClickClaimTicket = () => {
    // setUser((prevUser: User) => ({
    //   ...prevUser,
    //   redirectUrl: location.pathname,
    //   eventId: eventId,
    //   invitedId,
    //   isTicketedEvent,
    // }))
    // navigate(ROUTES.SIGNUP);
    // setStep(1);
    if (needSignUp) {
      setShowFastSignUpPopUp(true)
    } else {
      claimTicket()
    }
  }

  return {
    isLoading: state.isLoading,
    eventDetails: state.eventDetails,
    eventType: state.eventType,
    hasNotJoined,
    isAdmin,
    myEventActions,
    isDeclined,
    isRefunded,
    onLeaveEvent,
    canLeaveEvent,
    isPageLoading:
      leaveEventLoading ||
      joinEventLoading ||
      leaveNonTicketedEventLoading ||
      joinWaitlistFnLoading,
    myTicketStatus,
    onLeaveNonTicketedEvent,
    isNonTicketedReasonPopupOpen,
    handleOpenNonTicketedReasonPopup,
    handleCloseNonTicketedReasonPopup,
    onConfirmLeaveNonTicketedEvent,
    isUserEmailExist,
    onVerifyEmail,
    isEmailInboxVerification,
    loadingEmailVerification: isLoading,
    isVerificationPopup,
    setIsVerificationPopup,
    setIsEmailInboxVerification,
    refundAlertVisible,
    setRefundAlertVisible,
    refundReferenceNumber,
    refresh,
    isDraftEvent,
    plusOnePopupState,
    setPlusOnePopupState,
    plusOnes,
    acceptedGuestAvatars: validateAcceptedGuestAvatars,
    acceptedGuestAvatarsLoading,
    onEventJoin,
    guestList,
    isGuestListLoading,
    refetchGuestList,
    state,
    onClickClaimTicket,
    isOpenGuestList,
    setIsOpenGuestList,
    myJoinStatus,
    isCancelEventModalOpen,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleEventCancelled,
    handleShare,
  }
}
